'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import GamesCards from '@/app/[locale]/(main)/casino/components/games-cards';
import GamesSectionHeader from '@/app/[locale]/(main)/casino/components/games-section-header';
import CardsSection from '@/components/common/cards-section';
import type { GameListResponseType } from '@/lib/fetch';
import {
  type GamesListPaginatedResponse as GamesPage,
  fetchGamesListWithPagination,
} from '@/lib/fetch/casino-games/next-client';
import { getCasinoCategoryIcon } from '@/lib/utils/casino-categories';
import { useTenant } from '@/providers/TenantProvider';

type GamesListSectionProps = {
  isCarousel?: boolean;
  type: string;
  title?: string;
  itemClassName?: string;
  sectionClassName?: string;
  pageLimit?: number;
};

const GamesListSection = ({
  isCarousel = false,
  type,
  title,
  itemClassName,
  sectionClassName,
  pageLimit = 30,
}: GamesListSectionProps) => {
  const { ref: intersectionRef, inView } = useInView();

  const { tenantMachineName } = useTenant();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<GamesPage, Error>({
    queryKey: ['games', type, tenantMachineName],
    queryFn: ({ pageParam = 1 }) =>
      fetchGamesListWithPagination({
        categoryId: type,
        providerId: 'all-providers',
        page: pageParam as number,
        limit: pageLimit,
        tenantMachineName,
      }),
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);

  const allGames = data?.pages.flatMap((page) => page.games) ?? [];

  const totalGamesCount = data?.pages[0]?.total ?? 0;

  const Icon = getCasinoCategoryIcon(type);

  return (
    <CardsSection
      isCarousel={isCarousel}
      className={sectionClassName}
      headerRenderer={
        title && (
          <GamesSectionHeader
            title={title}
            Icon={Icon}
            type={type}
            gamesLength={totalGamesCount}
          />
        )
      }
    >
      <GamesCards
        isCarousel={isCarousel}
        isLoading={isLoading}
        data={allGames as GameListResponseType}
        itemClassName={itemClassName}
        isFetchingNextPage={isFetchingNextPage}
        intersectionRef={intersectionRef}
        pageLimit={pageLimit}
      />
    </CardsSection>
  );
};

export default GamesListSection;
